import { Controller } from '@hotwired/stimulus'
import flatpickr from 'flatpickr'

export default class extends Controller {
  static values = {
    options: Object,
    defaultDate: String,
    allowedDays: Array,
    minDate: String,
    clearable: Boolean,
  }

  connect() {
    const defaultOptions = {
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'F j, Y',
      allowInput: true,
      disableMobile: true,
    }

    // Set defaultDate if provided
    if (this.hasDefaultDateValue) {
      defaultOptions.defaultDate = this.defaultDateValue
    }

    // Set minDate if provided
    if (this.hasMinDateValue) {
      defaultOptions.minDate = this.minDateValue
    }

    // Add day-of-week filtering if allowedDays is specified
    if (this.hasAllowedDaysValue && this.allowedDaysValue.length > 0) {
      defaultOptions.enable = [
        (date) => this.allowedDaysValue.includes(date.getDay())
      ]
    }

    const options = { ...defaultOptions, ...this.optionsValue }
    this.flatpickr = flatpickr(this.element, options)

    // Prevent keyboard from opening on mobile and iOS zoom
    if (this.flatpickr.altInput) {
      this.flatpickr.altInput.id = this.element.id
      this.element.removeAttribute('id')
      this.flatpickr.altInput.inputMode = 'none'
      this.flatpickr.altInput.readOnly = true
      // Ensure font-size is at least 16px to prevent iOS zoom
      this.flatpickr.altInput.style.fontSize = '16px'
    }

    if (this.clearableValue) {
      this.addClearButton()
    }
  }

  disconnect() {
    this.clearButton?.remove()

    if (this.flatpickr) {
      this.flatpickr.destroy()
    }
  }

  get visibleInput() {
    return this.flatpickr.altInput || this.element
  }

  // Optional "clear" affordance for nullable dates. The alt input is read-only, so
  // without this there is no way to unset a date once one has been picked.
  addClearButton() {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'btn btn-outline-secondary'
    button.setAttribute('aria-label', 'Clear date')
    button.innerHTML = '<i class="bi bi-x-lg"></i>'
    button.addEventListener('click', () => {
      // clear() fires onChange, which detaches the button below — hand focus
      // back or keyboard users land on <body> and lose their place in the form.
      this.flatpickr.clear()
      this.visibleInput.focus()
      this.flatpickr.close() // focusing the input reopens the calendar
    })

    this.clearButton = button
    this.inputGroup = this.element.closest('.input-group')
    this.toggleClearButton()
    this.flatpickr.config.onChange.push(() => this.toggleClearButton())
  }

  // Attach/detach rather than hiding: a hidden button is still the input group's
  // last child, which would leave the date input with square right-hand corners.
  toggleClearButton() {
    if (!this.clearButton) return

    if (this.element.value === '') {
      this.clearButton.remove()
    } else if (this.inputGroup) {
      this.inputGroup.appendChild(this.clearButton)
    } else {
      this.visibleInput.insertAdjacentElement('afterend', this.clearButton)
    }
  }
}
